<!--
  - Copyright (c) ProviderTrust LLC.
  - All Rights Reserved.
  -
  - This software is confidential and proprietary information of
  - ProviderTrust LLC ("Confidential Information"). You shall not disclose
  - such Confidential Information and shall use it only in
  - accordance with the terms of the license agreement you entered
  - into with ProviderTrust.
  -->

<!-- suppress ALL -->
<template>
  <div>
    <div class="app-registration-container">
      <ul class="app-visual-step-bar">
        <li :class="{ 'app-chevron-item-complete': this.currentStep >= 1 }">
          <span>Choose your Search Types</span>
        </li>
        <li :class="{ 'app-chevron-item-complete': this.currentStep >= 2 }">
          <span>Column Mapping</span>
        </li>
        <li :class="{ 'app-chevron-item-complete': this.currentStep >= 3 }">
          <span>Additional Data Assignments</span>
        </li>
        <li :class="{ 'app-chevron-item-complete': this.currentStep >= 4 }">
          <span>Review</span>
        </li>
      </ul>
      <v-snackbar
        top
        :timeout="10000"
        :color="toasterMessage.mode"
        :value="toasterMessage.text"
        ref="testSnackBar"
      >
        <span v-html="toasterMessage.text" />

        <a @click.stop="clearMessage"> Close </a>
      </v-snackbar>
    </div>

    <div class="section" v-show="currentStep == 1">
      <v-card class="upload-file-upload">
        <div class="orange-top" />
        <div class="inner-container-file-upload">
          <h1>
            Please select the types of searches you would like to conduct below:
          </h1>
          <p>
            The file you upload will need to contain the correct criteria to
            perform your desired searches. Rows without a given piece of data
            will not be searched.
            <a
              target="_blank"
              href="https://help.exclusioncheck.com/hc/en-us/articles/360012297473"
              >Click here for more info on search types and required
              criteria.</a
            >
          </p>
          <div v-if="checkSearchTypes(true)">
            <h2>Exclusion Search</h2>
            <v-radio-group
              class="radio-group-search-type"
              v-model="selectedSearchTypesRadio"
              :mandatory="false"
              @change="checkNPI"
            >
              <v-radio
                v-for="item in searchTypes"
                :key="item.name"
                :label="item.name"
                :value="item"
                v-if="item.required"
              />
            </v-radio-group>
          </div>
          <div v-if="checkSearchTypes(false)">
            <table class="check-box-file-upload">
              <thead>
                <tr>
                  <th>Other Search Types</th>
                  <th>Search input needed</th>
                </tr>
              </thead>
              <tbody class="search-types-table">
                <tr
                  v-for="(item, indx) in searchTypes"
                  :key="item.name"
                  v-if="item.required == false"
                >
                  <td>
                    <input
                      type="checkbox"
                      :id="`checkbox${indx}`"
                      :value="item"
                      :key="item.name"
                      v-model="selectedSearchTypes"
                      @change="checkNPI"
                    />
                    <label :for="`checkbox${indx}`">{{ item.name }}</label>
                  </td>
                  <td>
                    <div>{{ item.requiredColumns[0] }}</div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <div v-show="!hideFileUploadDropZone">
            <h1>Select the file you wish to upload</h1>
            <vue-dropzone
              id="flexBatchUpload"
              ref="flexBatchUpload"
              :options="dropzoneOptions"
              @vdropzone-success="setupColumnMappings"
              @vdropzone-file-added="fileAdded"
              class="flex-dropzone"
            />
          </div>
          <div class="action-links-dialog">
            <a
              class="alignleft-links"
              @click.stop="
                navigateToDashboard()
                reset()
              "
              >Cancel</a
            >
            <a
              class="alignright-links"
              v-show="showLink"
              ref="testUploadFileLink"
              @click.stop="uploadingFile"
              >Upload and continue</a
            >
          </div>
        </div>
      </v-card>
    </div>

    <div class="section" v-show="currentStep == 2">
      <v-card class="upload-column-mapping">
        <div class="orange-top" />
        <div class="inner-container-column-mapping">
          <h1>Match your columns to your required search criteria below</h1>
          <p>
            Use the dropdown menus in the righthand column to select the data
            needed to perform the searches you selected.
          </p>
          <v-alert type="error" :value="columnMappingError">
            {{ columnMappingError }}
          </v-alert>

          <v-data-table
            class="column-mapping-table"
            :headers="columnMappingHeaders"
            :items="columnMappings"
            hide-actions
          >
            <template slot="items" slot-scope="props">
              <td>{{ props.item[0] }}</td>
              <td>{{ props.item[1] }}</td>
              <td>
                <v-select
                  :ref="`testsSelect${props.index}`"
                  :items="
                    requiredColumnDefinitions(
                      selectedRequiredColumnDefinitions[props.index]
                    )
                  "
                  v-model="selectedRequiredColumnDefinitions[props.index]"
                  class="column-mapping-select"
                  solo
                  :clearable="true"
                  dense
                />
              </td>
            </template>
          </v-data-table>
          <h1>
            If you are using a unique identifier, please specify which column
            below
          </h1>
          <p>
            A unique identifier is any numeric value you wish to use to identify
            individuals. Common unique identifiers include SSN, NPI, or your
            organization’s employee ID number.
            <a
              target="_blank"
              href="https://help.exclusioncheck.com/hc/en-us/articles/360012299933"
              >For more info on unique identifiers and why you might benefit
              from using one, click here.</a
            >
          </p>
          <v-select
            :items="columnMappingList"
            v-model="uniqueColumnMapping"
            solo
            class="column-mapping-select"
            :clearable="true"
            dense
            style="width: 20rem;"
          />
          <div class="action-links-dialog">
            <a class="alignleft-links" @click.stop="currentStep -= 1">
              <v-icon class="angle-left">fa fa-angle-left </v-icon>
              Return to search selection & upload</a
            >
            <a class="alignright-links" @click.stop="requiredColumnsCheck"
              >{{
                !this.showAdditionalDataSection
                  ? "Continue to Review"
                  : "Continue to select additional data"
              }}
              <v-icon class="angle-right">fa fa-angle-right </v-icon>
            </a>
          </div>
        </div>
      </v-card>
    </div>

    <div class="section" v-show="currentStep == 3">
      <v-card class="upload-column-mapping">
        <div class="orange-top" />
        <div class="inner-container-column-mapping">
          <h1>Match your columns with any additional data</h1>
          <p>
            Do you have additional data you would like to upload to help review
            results? Select from your file using the dropdowns below. This data
            will NOT be used as a part of your search. This data will show next
            to records returned to help you review and resolve potential
            matches.
          </p>
          <v-alert type="error" :value="columnMappingError">
            {{ columnMappingError }}
          </v-alert>

          <v-data-table
            class="column-mapping-table"
            :headers="columnMappingCorrelatedHeaders"
            :items="columnMappings"
            hide-actions
          >
            <template slot="items" slot-scope="props">
              <td>{{ props.item[0] }}</td>
              <td>{{ props.item[1] }}</td>
              <td>{{ selectedRequiredColumnDefinitions[props.index] }}</td>
              <td>
                <v-checkbox
                  v-model="selectedAdditionalColumnDefinitions[props.index]"
                  v-if="!selectedRequiredColumnDefinitions[props.index]"
                  :value="String(props.index)"
                />
              </td>
            </template>
          </v-data-table>
          <div class="action-links-dialog">
            <a class="alignleft-links" @click.stop="currentStep -= 1">
              <v-icon class="angle-left">fa fa-angle-left </v-icon>
              Return to search criteria mapping
            </a>
            <a class="alignright-links" @click.stop="currentStep += 1"
              >Review selections
              <v-icon class="angle-right">fa fa-angle-right </v-icon>
            </a>
          </div>
        </div>
      </v-card>
    </div>

    <div class="section" v-if="currentStep == 4">
      <v-card class="upload-review-mappings">
        <div class="orange-top" />
        <div class="inner-container-review-mappings">
          <h1>Double check that everything looks good</h1>
          <div class="search-type-list">
            <div class="header">
              <h2>You're about to run these searches</h2>
              <a @click.stop="currentStep = 1">Edit</a>
            </div>

            <line-item-card
              :items="selectedSearchTypes.concat(selectedSearchTypesRadio)"
              class="search-types"
            />
          </div>

          <div class="section-border" />
          <div class="column-mappings">
            <div class="header">
              <h2>
                You selected these columns from your file as search criteria:
              </h2>
              <a @click.stop="currentStep = 2">Edit</a>
            </div>
            <div class="review-tables">
              <table class="required-data">
                <thead>
                  <tr>
                    <th>Column label (from file)</th>
                    <th>Mapped search criteria</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="item in columnMappingsReview()" :key="item">
                    <td>{{ item.name }}</td>
                    <td>{{ item.description }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div class="unique-mapping" v-if="showUnique()">
              <h2>Column with unique identifier:</h2>
              <p>{{ this.uniqueColumnMapping }}</p>
            </div>
          </div>

          <div
            class="additional-column-mappings"
            v-if="
              showAdditionalDataSection ||
                selectedAdditionalColumnDefinitions.length > 0
            "
          >
            <div class="section-border" />
            <div class="header">
              <h2>You picked these columns to upload as additional data</h2>
              <a @click.stop="currentStep = 3">Edit</a>
            </div>
            <div class="review-tables">
              <table
                class="additional-data"
                v-if="getMappedCorrelatedColumns().length > 0"
              >
                <thead>
                  <tr>
                    <th>Column label (from file)</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="item in getMappedCorrelatedColumns()" :key="item">
                    <td>{{ item }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
          <div class="review-links-dialog">
            <v-btn class="start-btn" @click.stop="validate()"
              >Start Search
            </v-btn>
            <a class="alignleft-links" @click.stop="isAdditionalDataRequired">
              <v-icon class="angle-left">fa fa-angle-left </v-icon>
              {{
                !this.showAdditionalDataSection
                  ? "Return to Column Mapping"
                  : "Return to Additional Data"
              }}
            </a>
          </div>
        </div>
      </v-card>
    </div>

    <div class="section" v-show="currentStep == 5">
      <v-card class="upload-errors">
        <div class="orange-top" />
        <div class="inner-container-errors">
          <h1>It looks like a couple of items need your attention</h1>
          <p>
            Please see the table below for a list of errors found in your file.
            Once these have been fixed, please upload again.
          </p>
          <v-data-table
            :headers="validationResultHeaders"
            :items="validationErrors.error"
            :pagination.sync="pagination"
            hide-actions
          >
            <template slot="items" slot-scope="props">
              <td>{{ props.item.rowIndx + 1 }}</td>
              <td>{{ props.item.errorMessage }}</td>
              <td>{{ props.item.data }}</td>
            </template>
          </v-data-table>
          <div class="text-xs-center pt-2">
            <v-pagination
              v-model="pagination.page"
              :length="this.pagination.total + 1"
              :total-visible="7"
            />
          </div>
          <h1>
            Once you have fixed the above errors, re-upload your corrected file
            below:
          </h1>
          <p>
            Your file will keep the search options and mappings previously
            selected and you will have a chance to review again prior to
            searching.
          </p>
          <vue-dropzone
            id="flexBatchUpload"
            ref="errorflexBatchUpload"
            :options="dropzoneOptions"
            @vdropzone-success="checkErrorFileUpload"
            @vdropzone-file-added="errorFileAdded"
            class="flex-dropzone"
          />
          <div class="action-links-dialog">
            <a class="alignleft-links" @click.stop="currentStep = 4">
              <v-icon class="angle-left">fa fa-angle-left </v-icon>
              Return to Review Selection
            </a>
            <a
              class="alignright-links"
              v-show="showErrorReviewLink"
              @click.stop="checkRedoColumnMapping"
              >{{ this.goRedoColumnMapping ? "Column Mapping" : "Review" }}
              <v-icon class="angle-right">fa fa-angle-right </v-icon>
            </a>
          </div>
        </div>
      </v-card>
    </div>
  </div>
</template>

<script>
import vueDropzone from "vue2-dropzone"
import axios from "axios"

import { dedupeArry, transposeArray } from "@/service/util"
import { API } from "@/common/constants"
import LineItemCard from "@/components/LineItemCard"

import toasterMixin from "@/mixins/toaster"

export default {
  name: "FlexSearchFileUpload",
  mixins: [toasterMixin],

  data: () => ({
    dropzoneOptions: {
      url: API.UPLOAD_ENDPOINT,
      thumbnailWidth: 150,
      headers: {
        Accept: "text/plain, application/json, */*" // the default is json which is not compatible with our ErrorResponse.java
      },
      params: {
        typeName: "Flex"
      },
      dictDefaultMessage:
        "<div>" +
        "<i class='fa fa-plus-circle' " +
        "style='text-align: center;color: #4485B5;\n" +
        "  font-size: 1.5rem;'></i>" +
        "<div " +
        "  style='color: #4485B5;" +
        "  font-size: 1rem;" +
        "  padding-top: 1rem;" +
        "  text-align: center;'>Drag and drop or click here to browse and select your file</div>" +
        "</div>",
      maxFiles: 1,
      parallelUploads: 1,
      init: function() {
        this.on("maxfilesexceeded", function(file) {
          alert("No more files please!")
          this.removeFile(file)
        })

        this.on("processing", function(file) {
          if (file.name.toLowerCase().match(/\.csv$/) === null) {
            this.removeFile(file)
            alert("File type not supported, please upload a .CSV file.")
          }
        })
      }
    },

    columnMappingHeaders: [
      { text: "Column Label from CSV", sortable: false },
      { text: "Preview Data", sortable: false },
      { text: "Pick a Search Criteria", sortable: false }
    ],

    columnMappingCorrelatedHeaders: [
      { text: "Column Label from CSV", sortable: false },
      { text: "Preview Data", sortable: false },
      { text: "Pick a Search Criteria", sortable: false },
      { text: "Include as Addt'l Data", sortable: false }
    ],

    validationResultHeaders: [
      { text: "Row Number", sortable: false },
      { text: "Error Message", sortable: false },
      { text: "File Data", sortable: false }
    ],

    searchTypes: [],

    selectedSearchTypes: [],

    columnMappings: [],

    uniqueColumnMapping: {},

    selectedRequiredColumnDefinitions: [],

    selectedAdditionalColumnDefinitions: [],

    columnMappingError: "",

    validationErrors: [],

    showBatchOptions: [],

    disabledLink: false,

    previouslySelected: [],

    showLink: false,

    showErrorReviewLink: false,

    pagination: {
      total: 0,
      rowsPerPage: 4
    },

    currentStep: 1,

    goRedoColumnMapping: false,

    hideFileUploadDropZone: false
  }),

  methods: {
    navigateToDashboard() {
      this.$router.push("/flex-search")
    },
    reset() {
      this.selectedSearchTypes = []
      this.columnMappings = []
      this.uniqueColumnMapping = {}
      this.selectedRequiredColumnDefinitions = []
      this.selectedAdditionalColumnDefinitions = []
      this.columnMappingError = ""
      this.validationErrors = []
      this.disabledLink = false
      this.previouslySelected = []
      this.showLink = false
      this.showErrorReviewLink = false
      this.hideFileUploadDropZone = false
      this.currentStep = 1
      axios.get(API.SEARCHTYPE_ENDPOINT).then(response => {
        this.searchTypes = response.data
      })
      this.resetVueDropzonesFiles()
    },

    resetVueDropzonesFiles() {
      this.$refs.flexBatchUpload.removeAllFiles()
      this.$refs.errorflexBatchUpload.removeAllFiles()
    },

    validate() {
      const mappedColumns = this.getMappedColumns((columnMapping, indx) => indx)

      axios
        .post(
          API.COLUMNMAPPING_ENDPOINT.replace(":batchId", this._batchId),
          {
            columnMappings: mappedColumns,
            searchTypes: dedupeArry(
              this.selectedSearchTypes.concat(this.selectedSearchTypesRadio),
              []
            ).map(searchType => searchType.name),
            correlatedColumnIndexes: this.selectedCorrelatedData,
            uniqueIdIndex: this.uniqueColumnIndex
          },
          {
            params: { dryrun: true }
          }
        )
        .then(
          () => {
            this.submit(mappedColumns)
          },
          error => {
            this.validationErrors = error.response.data
            this.resetVueDropzonesFiles()
            this.pagination.total = Math.floor(
              this.validationErrors.error.length / this.pagination.rowsPerPage
            )
            this.currentStep = 5
            this.hideFileUploadDropZone = true
          }
        )
    },
    submit(mappedColumns) {
      axios
        .post(API.COLUMNMAPPING_ENDPOINT.replace(":batchId", this._batchId), {
          columnMappings: mappedColumns,
          searchTypes: dedupeArry(
            this.selectedSearchTypes.concat(this.selectedSearchTypesRadio),
            []
          ).map(searchType => searchType.name),
          correlatedColumnIndexes: this.selectedCorrelatedData,
          uniqueIdIndex: this.uniqueColumnIndex
        })
        .then(
          () => {
            this.showMessage(
              "Batch search configuration is created successfully"
            )
            this.reset()
            this.$router.push("/flex-search")
          },
          error => {
            this.showMessage(
              `Failed to create batch search configuration.`,
              "error"
            )
            this.validationErrors = error.response.data
            this.pagination.total = Math.floor(
              this.validationErrors.error.length / this.pagination.rowsPerPage
            )
            this.hideFileUploadDropZone = true
            this.currentStep = 5
            this.resetVueDropzonesFiles()
          }
        )
    },

    checkStartButtonPermission() {
      return this.searchTypes.length === 0
    },

    checkSearchTypes(required) {
      return this.searchTypes.some(el => el.required === required)
    },

    checkNPI() {
      if (
        this.npiValidationIndex !== -1 &&
        this.selectedSearchTypes.indexOf(
          this.searchTypes[this.npiValidationIndex]
        ) === -1
      ) {
        for (let i = 0; i < this.selectedSearchTypes.length; i++) {
          if (this.selectedSearchTypes[i].name === "NPI Medicare OptOut") {
            this.selectedSearchTypes.push(
              this.searchTypes[this.npiValidationIndex]
            )
          }
        }
        this.checkNPIRadio()
      }
    },

    checkNPIRadio() {
      if (this.selectedSearchTypesRadio.name === "Search by NPI") {
        this.selectedSearchTypes.push(this.searchTypes[this.npiValidationIndex])
      }
    },

    setupColumnMappings(file, response) {
      this.columnMappings = transposeArray(response.previewData)
      this._batchId = response.batchId
      this.showLink = true
    },

    checkErrorFileUpload(file, response) {
      if (
        this.columnMappings.length <=
        transposeArray(response.previewData).length
      ) {
        this.columnMappings = transposeArray(response.previewData)
        this._batchId = response.batchId
      } else {
        this.showMessage(
          "Opps a problem has occurred please redo mapping",
          "error"
        )
        this.goRedoColumnMapping = true
        this.columnMappings = transposeArray(response.previewData)
        this._batchId = response.batchId
        this.selectedAdditionalColumnDefinitions = []
        this.selectedRequiredColumnDefinitions = []
        this.columnMappingError = ""
        this.validationErrors = []
        this.previouslySelected = []
      }
    },

    checkRedoColumnMapping() {
      this.goRedoColumnMapping ? (this.currentStep = 2) : (this.currentStep = 4)
    },

    getMappedColumns(indxFunc) {
      return this.selectedRequiredColumnDefinitions.reduce(
        (acc, currentVal, currentIndx) => {
          // workaround util https://github.com/vuetifyjs/vuetify/issues/4712 is fixed
          var bug4712Workaround = this.requiredColumns.includes(currentVal)

          if (currentVal && bug4712Workaround)
            acc[
              indxFunc(this.columnMappings[currentIndx], currentIndx)
            ] = currentVal
          return acc
        },
        {}
      )
    },

    getMappedCorrelatedColumns() {
      var array = []
      for (let i = 0; i < this.columnMappings.length; i++) {
        if (this.selectedAdditionalColumnDefinitions[i]) {
          array.push(this.columnMappings[i][0])
        }
      }
      return array
    },

    columnMappingsReview() {
      return Object.entries(
        this.getMappedColumns(previewRow => previewRow[0])
      ).map(([columnName1, columnName2]) => ({
        name: columnName1,
        description: columnName2
      }))
    },

    fileAdded: function(file) {
      this.disabledLink = true
    },

    errorFileAdded: function(file) {
      this.showErrorReviewLink = true
    },

    uploadingFile() {
      if (!this.disabledLink) {
        this.showMessage("Please Upload a file and try again", "error")
        return
      }

      if (
        this.disabledLink &&
        (this.selectedSearchTypesRadio.length !== 0 ||
          this.selectedSearchTypes.length)
      ) {
        this.currentStep += 1
      } else {
        this.showMessage("Please select a search", "error")
      }
    },

    requiredColumnsCheck() {
      if (this.requiredMappings.length === 0) {
        if (this.showAdditionalDataSection) {
          this.currentStep += 1
        } else {
          this.currentStep += 2
        }
      } else {
        this.showMessage(
          "Please complete this required mapping " + this.requiredMappings[0],
          "error"
        )
      }
    },

    isAdditionalDataRequired() {
      if (this.showAdditionalDataSection) {
        this.currentStep -= 1
      } else {
        this.currentStep -= 2
      }
    },

    showUnique() {
      if (this.uniqueColumnMapping === null) {
        return false
      } else return JSON.stringify(this.uniqueColumnMapping) !== "{}"
    }
  },

  computed: {
    requiredColumnDefinitions() {
      return selectedColumn => {
        const unselectedColumns = this.requiredColumns.filter(
          column => !this.selectedRequiredColumnDefinitions.includes(column)
        )
        return selectedColumn && this.requiredColumns.includes(selectedColumn)
          ? unselectedColumns.concat(selectedColumn)
          : unselectedColumns
      }
    },

    showAdditionalDataSection() {
      return (
        !(
          this.requiredColumns.includes("NPI") ||
          this.requiredColumns.includes("SSN")
        ) ||
        this.requiredColumns.includes("First Name") ||
        this.requiredColumns.includes("Last Name") ||
        this.requiredColumns.includes("Company Name")
      )
    },

    requiredMappings() {
      return this.requiredColumns.filter(
        column => !this.selectedRequiredColumnDefinitions.includes(column)
      )
    },

    requiredColumns() {
      return dedupeArry(
        this.selectedSearchTypes
          .reduce(
            (acc, searchType) =>
              dedupeArry(acc.concat(searchType.requiredColumns)),
            []
          )
          .concat(this.selectedSearchTypesRadio.requiredColumns || [])
      )
    },

    selectedCorrelatedData() {
      var correlatedArray = []
      for (let i = 0; i < this.selectedRequiredColumnDefinitions.length; i++) {
        if (this.selectedRequiredColumnDefinitions[i]) correlatedArray.push(i)
      }

      return correlatedArray.concat(
        this.selectedAdditionalColumnDefinitions.filter(n => n)
      )
    },

    batchHistoryHeaders() {
      return [
        "",
        "File Name",
        "Date Uploaded",
        "Uploaded By",
        "Record Count",
        "To Review",
        "Status",
        "action"
      ].map(name => ({ text: name, sortable: false }))
    },

    selectedSearchTypesRadio: {
      get() {
        return this.previouslySelected
      },
      set(v) {
        if (v === this.previouslySelected) {
          this.previouslySelected = []
        } else {
          this.previouslySelected = v
        }
      }
    },

    columnMappingList() {
      var newArray = []
      this.columnMappings.forEach(function(row) {
        newArray.push(row[0])
      })
      return newArray
    },

    uniqueColumnIndex() {
      return this.columnMappingList.indexOf(this.uniqueColumnMapping)
    },

    npiValidationIndex() {
      for (let i = 0; i < this.searchTypes.length; i++) {
        if (this.searchTypes[i].name === "NPI Validation") {
          return i
        }
      }
      return -1
    }
  },
  created() {
    axios.get(API.SEARCHTYPE_ENDPOINT).then(response => {
      this.searchTypes = response.data
    })
  },

  components: {
    vueDropzone,
    LineItemCard
  }
}
</script>

<style lang="scss" scoped>
.app-registration-container {
  max-width: 150rem;
  margin: 0.5rem auto 2rem auto;
  text-align: center;
}

.app-visual-step-bar {
  max-width: 60rem;
  margin: 0 auto 3rem auto;
  @include app-chevron-bar;
}

.angle-left {
  font-size: 1rem;
  color: #4485b5;
  padding-bottom: 0.1rem;
  padding-right: 0.375rem;
}

.angle-right {
  font-size: 1rem;
  color: #4485b5;
  padding-bottom: 0.1rem;
  padding-left: 0.375rem;
}

.section-border {
  border: 1px solid #c7c7c7;
  width: 498px;
  height: 0.1px;
  margin-bottom: 1.5rem;
  margin-top: 1.5rem;
}

.flex-dropzone {
  height: 7rem;
  width: 100%;
  margin: 0;
  padding: 0;
  background-color: #f0f0f0;
}

.action-links-dialog {
  padding-top: 2rem;
  padding-bottom: 3rem;
  position: flex;

  .alignleft-links {
    float: left;
    text-decoration: underline;
  }

  .alignright-links {
    float: right;
    text-decoration: underline;
  }
}

.orange-top {
  @include orange-top;
}

.radio-group-search-type {
  padding: 0;
}

/deep/ .v-table {
  /* the table headers */
  thead tr {
    background-color: $color-ec-blue;

    .column-mapping-select .input-group {
      padding: 0;
      top: 0.375rem;

      .input-group--text-field label {
        top: 0;
      }
    }

    th {
      color: $color-white;
      font-size: 1rem;
      font-weight: $normal;
      text-align: left;
      padding-right: 0.375rem !important;
      padding-left: 0.375rem !important;
      height: 2rem;
    }
  }
  /* the data rows */
  tbody {
    tr:nth-child(even) {
      background-color: $color-off-white;
    }

    tr td {
      color: $color-ec-black1;
      font-size: 0.875rem;
      font-weight: $normal;
      text-align: left;
      padding-right: 0.375rem !important;
      padding-left: 0.375rem !important;
      height: 2.75rem;

      a {
        text-decoration: underline;
      }

      p {
        margin-bottom: 0;
      }
    }
  }
}

.upload-file-upload {
  width: 34.875rem;
  max-height: 49rem;
  background-color: $color-white;
  box-shadow: 0.0625rem 0.1875rem 0.1875rem 0 rgba(170, 170, 170, 0.5);
  margin: auto;

  .inner-container-file-upload {
    padding-left: 1.875rem;
    padding-right: 1.875rem;
    text-align: left;

    h1 {
      padding-top: 1.4rem;
      padding-bottom: 0.7rem;
      font-weight: $heavy;
      font-size: 1.5rem;
    }

    p {
      padding-bottom: 0.75rem;
    }

    h2 {
      font-size: 1rem;
      font-weight: $heavy;
    }

    .check-box-file-upload {
      width: 100%;
      thead {
        th {
          border-bottom: 1px solid #c7c7c7;
          text-align: left;
        }
        tr th:last-child {
          text-align: right;
        }
      }

      tbody {
        tr {
          height: 2rem;
        }

        td {
          border-bottom: 1px solid #c7c7c7;
          input {
            text-align: center;
          }
        }
        tr td:last-child {
          text-align: right;

          .required-column {
            padding-bottom: 0;
          }
        }
      }
    }

    .search-types-table {
      list-style: none;
      li {
        border-top: 1px solid #c7c7c7;
        border-bottom: 1px solid #c7c7c7;
      }
      clear: float;
    }

    .other-searchtype-headers {
      display: flex;
      justify-content: space-between;
    }

    .action-links-dialog {
      .alignleft-links {
        color: #aa0000;
      }
    }
  }
}

.upload-column-mapping {
  width: 64rem;
  background-color: $color-white;
  box-shadow: 0.0625rem 0.1875rem 0.1875rem 0 rgba(170, 170, 170, 0.5);
  overflow-x: unset;
  margin: auto;

  /deep/ .column-mapping-select {
    .v-input__control {
      color: #4a4a4a;
      font-weight: $normal;
      line-height: 1.125rem;
      background-color: #ffffff;

      height: 1.875rem;
      box-shadow: none;
      border: 0.0625rem solid #e0e0e0;
      width: 20rem;

      min-height: auto;
      .v-input__slot {
        margin: 0;
        padding-right: 0.3rem;
      }
    }
  }

  .inner-container-column-mapping {
    padding-left: 1.875rem;
    padding-right: 1.875rem;
    text-align: left;

    h1 {
      padding-top: 1.4rem;
      padding-bottom: 0.7rem;
      font-weight: $heavy;
      font-size: 1.5rem;
    }

    p {
      padding-bottom: 0.75rem;
    }

    .v-input__control {
      color: #4a4a4a;
      font-weight: $normal;
      line-height: 1.125rem;
      background-color: #ffffff;

      height: 1.875rem;
      box-shadow: none;
      border: 1px solid #e0e0e0;
      width: 5rem;

      min-height: auto;
      .v-input__slot {
        margin: 0;
      }
    }

    .action-links-dialog {
      height: 1.5rem;
      padding-bottom: 4rem;
      padding-top: 1.5rem;
    }

    /deep/ .v-table__overflow {
      overflow-x: inherit;
      overflow-y: inherit;
      /* the table headers */
      thead tr {
        background-color: $color-ec-blue;

        th {
          color: $color-white;
          height: 2rem;
          font-size: 1rem;
          font-weight: $normal;
          text-align: left;
          padding-right: 0.375rem !important;
          padding-left: 0.375rem !important;
        }

        th:last-child {
          width: 18rem;
        }
      }
      /* the data rows */
      tbody {
        tr:nth-child(even) {
          background-color: $color-off-white;
        }

        tr {
          height: 1rem;

          td {
            color: $color-ec-black1;
            font-size: 0.875rem;
            font-weight: $normal;
            text-align: left;
            padding-right: 0.375rem !important;
            padding-left: 0.375rem !important;

            label {
              color: $color-ec-black1;
              padding-bottom: 0.43rem;
            }

            .v-input__control {
              padding-top: 0.175rem;
              height: 2rem;
              .v-input__slot {
                margin-bottom: 0;
              }
            }
          }
        }
      }
    }
  }
}

.upload-review-mappings {
  width: 34.875rem;
  height: auto;
  background-color: $color-white;
  box-shadow: 0.0625rem 0.1875rem 0.1875rem 0 rgba(170, 170, 170, 0.5);
  margin: auto;

  /deep/ .item-row {
    border-bottom: none;
  }

  .inner-container-review-mappings {
    padding-left: 1.875rem;
    padding-right: 1.875rem;

    text-align: left;

    h1 {
      padding-top: 1rem;
      padding-bottom: 1.5rem;
      font-size: 1.5rem;
      font-weight: $heavy;
    }

    .unique-mapping {
      padding-left: 0.75rem;
      h2 {
        display: inline;
        font-size: 1rem;
        font-weight: $heavy;
        text-align: left;
      }

      p {
        display: inline;
      }
    }

    .header {
      padding-bottom: 1rem;
      h2 {
        font-size: 1rem;
        font-weight: $heavy;
        text-align: left;
        float: left;
      }

      a {
        float: right;
      }
    }

    .search-type-list {
      .search-types {
        padding-left: 0.75rem;
        margin-bottom: 0;
        p {
          padding-bottom: 0;
        }
      }
    }

    .review-links-dialog {
      display: flex;
      flex-direction: column;
      align-items: center;
      padding-top: 1.5rem;
      padding-bottom: 1.25rem;

      .start-btn {
        background-color: $color-ec-blue;
        border-radius: 2px;
        box-shadow: 0 1px 1px 0 rgba(0, 0, 0, 0.23999999463558197),
          0 1px 1px 0 rgba(0, 0, 0, 0.11999999731779099);
        width: 17rem;
        height: 3rem;
        color: $color-white;
        font-size: 1.175rem;
        font-weight: $heavy;
        text-align: center;
      }
    }

    .review-tables {
      padding: 1.25rem 0.75rem 1rem;

      td {
        border-bottom: 1px solid #c7c7c7;
        height: 2rem;
        text-align: left;
      }

      th {
        border-bottom: 1px solid #c7c7c7;
        text-align: left;
      }

      .additional-data {
        width: 100%;
      }

      .required-data {
        width: 100%;
        td:last-child {
          text-align: right;
        }

        th:last-child {
          text-align: right;
        }
      }
    }
  }
}

.upload-errors {
  width: 64rem;
  height: auto;
  margin: auto;

  .inner-container-errors {
    padding-left: 1.875rem;
    padding-right: 1.875rem;

    h1 {
      padding-top: 1rem;
      font-size: 1.5rem;
      font-weight: $heavy;
    }
  }
}
</style>

<style lang="scss">
@import "~vue2-dropzone/dist/vue2Dropzone.min";

.dropzone .dz-preview {
  min-height: 0 !important;
  height: 100% !important;
  width: 100%;
  margin: 0;
  padding: 0;

  .dz-image {
    background-color: #4485b5 !important;
  }

  .dz-size {
    padding-top: 1rem;
    text-align: center;
  }
  .dz-filename {
    text-align: center;
    font-size: 1rem;
  }
}

.dropzone {
  padding: 0 !important;
}
</style>



// WEBPACK FOOTER //
// src/components/FlexSearchFileUpload.vue